import React, { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { 
  BookOpen, 
  Clock, 
  ArrowLeft, 
  CheckCircle2, 
  ShieldCheck, 
  Share2, 
  Sparkles, 
  ChevronRight, 
  ExternalLink,
  PhoneCall,
  Lock,
  ArrowRight,
  AlertTriangle,
  Info,
  Lightbulb,
  Check
} from 'lucide-react';
import { RESOURCE_ARTICLES } from '../data/resources';
import { useStore } from '../store/useStore';

export const ResourceDetailPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { openAiModal } = useStore();
  const [completedSteps, setCompletedSteps] = useState<Record<number, boolean>>({});

  const article = RESOURCE_ARTICLES.find(a => a.slug === slug) || RESOURCE_ARTICLES[0];
  const relatedArticles = RESOURCE_ARTICLES.filter(a => article.relatedSlugs?.includes(a.slug));

  const toggleStep = (idx: number) => {
    setCompletedSteps(prev => ({ ...prev, [idx]: !prev[idx] }));
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-10">
      {/* Breadcrumb / Back Link */}
      <div className="flex items-center justify-between">
        <Link
          to="/resources"
          className="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-white transition-colors"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          <span>Back to Knowledge Base</span>
        </Link>
        <span className="text-xs font-mono text-emerald-400 font-semibold uppercase bg-emerald-950 px-2.5 py-1 rounded-full border border-emerald-800/80">
          Australian Guidance
        </span>
      </div>

      {/* Article Header */}
      <div className="space-y-4 border-b border-slate-800 pb-6">
        <div className="flex items-center gap-2 text-xs">
          <span className="px-2.5 py-0.5 rounded-full bg-emerald-950 text-emerald-300 border border-emerald-800 font-bold uppercase">
            {article.category}
          </span>
          <span className="text-slate-500">•</span>
          <span className="text-slate-400 font-mono flex items-center gap-1">
            <Clock className="w-3.5 h-3.5" /> {article.readingTimeMinutes} min read
          </span>
          <span className="text-slate-500">•</span>
          <span className="text-slate-400">{article.date}</span>
        </div>

        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white leading-tight tracking-tight">
          {article.title}
        </h1>

        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pt-2">
          <div className="flex items-center gap-3">
            <img
              src={article.author.avatar}
              alt={article.author.name}
              className="w-10 h-10 rounded-full object-cover border border-emerald-500/40 shadow-sm"
            />
            <div className="text-xs">
              <div className="font-bold text-white">{article.author.name}</div>
              <div className="text-slate-400">{article.author.role}</div>
            </div>
          </div>

          <button
            onClick={openAiModal}
            className="px-4 py-2 rounded-xl bg-slate-900 hover:bg-slate-800 border border-slate-700 text-emerald-400 text-xs font-semibold flex items-center gap-1.5 transition-colors self-start sm:self-auto"
          >
            <Sparkles className="w-4 h-4" />
            <span>Ask AI About This Topic</span>
          </button>
        </div>
      </div>

      {/* Hero Image */}
      {article.imageUrl && (
        <div className="space-y-2">
          <div className="overflow-hidden rounded-3xl border border-slate-800 shadow-2xl">
            <img
              src={article.imageUrl}
              alt={article.title}
              className="w-full h-[320px] sm:h-[420px] object-cover hover:scale-105 transition-transform duration-700"
            />
          </div>
          {article.imageCaption && (
            <p className="text-xs text-slate-400 text-center italic">
              {article.imageCaption}
            </p>
          )}
        </div>
      )}

      {/* Key Takeaways Box */}
      <div className="p-6 sm:p-8 rounded-3xl bg-slate-900 border border-slate-800 space-y-4 shadow-xl">
        <h3 className="text-xs font-bold text-slate-300 uppercase tracking-wider flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-emerald-400" />
          <span>Key Takeaways for Australians</span>
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs text-slate-200">
          {article.keyTakeaways.map((point, idx) => (
            <div key={idx} className="flex items-start gap-2.5 bg-slate-950 p-3.5 rounded-xl border border-slate-800">
              <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
              <span className="leading-relaxed">{point}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Article Main Overview */}
      <div className="space-y-4 text-sm sm:text-base text-slate-300 leading-relaxed font-normal">
        {article.content.map((paragraph, idx) => (
          <p key={idx}>{paragraph}</p>
        ))}
      </div>

      {/* Subsections with callouts */}
      {article.sections && article.sections.length > 0 && (
        <div className="space-y-8 pt-4">
          {article.sections.map((section, sIdx) => (
            <div key={sIdx} className="space-y-4">
              <h2 className="text-xl sm:text-2xl font-bold text-white tracking-tight border-b border-slate-800 pb-2">
                {section.heading}
              </h2>

              <div className="space-y-3 text-sm sm:text-base text-slate-300 leading-relaxed">
                {section.body.map((para, pIdx) => (
                  <p key={pIdx}>{para}</p>
                ))}
              </div>

              {/* Callout Box if present */}
              {section.callout && (
                <div className={`p-4 sm:p-5 rounded-2xl border flex items-start gap-3.5 text-xs sm:text-sm ${
                  section.callout.type === 'warning'
                    ? 'bg-rose-950/30 border-rose-800/60 text-rose-200'
                    : section.callout.type === 'tip'
                      ? 'bg-amber-950/30 border-amber-800/60 text-amber-200'
                      : 'bg-emerald-950/30 border-emerald-800/60 text-emerald-200'
                }`}>
                  {section.callout.type === 'warning' && <AlertTriangle className="w-5 h-5 text-rose-400 flex-shrink-0 mt-0.5" />}
                  {section.callout.type === 'tip' && <Lightbulb className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />}
                  {section.callout.type === 'info' && <Info className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />}
                  <div className="space-y-1">
                    <div className="font-bold text-white">{section.callout.title}</div>
                    <div className="leading-relaxed opacity-95">{section.callout.text}</div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Secondary Image if available */}
      {article.secondaryImageUrl && (
        <div className="space-y-2 pt-4">
          <div className="overflow-hidden rounded-3xl border border-slate-800 shadow-xl">
            <img
              src={article.secondaryImageUrl}
              alt={article.title}
              className="w-full h-[260px] sm:h-[340px] object-cover"
            />
          </div>
          {article.secondaryImageCaption && (
            <p className="text-xs text-slate-400 text-center italic">
              {article.secondaryImageCaption}
            </p>
          )}
        </div>
      )}

      {/* Action Steps Interactive Checklist */}
      {article.actionSteps && article.actionSteps.length > 0 && (
        <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 sm:p-8 space-y-4 shadow-xl">
          <h3 className="text-base font-bold text-white flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-emerald-400" />
            <span>Interactive Action Checklist</span>
          </h3>
          <p className="text-xs text-slate-400">Click steps as you complete them to track your protection progress:</p>
          <div className="space-y-2.5">
            {article.actionSteps.map((step, idx) => {
              const isChecked = !!completedSteps[idx];
              return (
                <div
                  key={idx}
                  onClick={() => toggleStep(idx)}
                  className={`p-3.5 rounded-xl border cursor-pointer transition-all flex items-start gap-3 text-xs ${
                    isChecked
                      ? 'bg-emerald-950/40 border-emerald-600 text-emerald-200 line-through opacity-80'
                      : 'bg-slate-950 border-slate-800 text-slate-200 hover:border-slate-700'
                  }`}
                >
                  <div className={`w-5 h-5 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 ${
                    isChecked ? 'bg-emerald-600 text-white' : 'border border-slate-600'
                  }`}>
                    {isChecked && <Check className="w-3.5 h-3.5" />}
                  </div>
                  <span className="leading-relaxed flex-1">{step}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* HIGH-CONVERTING SIGNUP CALL TO ACTION (CTA) */}
      <div className="bg-gradient-to-br from-emerald-950 via-slate-900 to-navy-950 border-2 border-emerald-500/80 rounded-3xl p-8 sm:p-10 shadow-2xl space-y-6 text-center sm:text-left relative overflow-hidden">
        <div className="absolute top-0 right-0 w-80 h-80 bg-emerald-500/10 blur-[100px] rounded-full pointer-events-none -z-10" />

        <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="space-y-2 max-w-xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-900/80 text-emerald-300 text-xs font-bold border border-emerald-700">
              <ShieldCheck className="w-3.5 h-3.5" />
              <span>PROTECT YOUR AUSTRALIAN IDENTITY TODAY</span>
            </div>
            <h3 className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">
              Don't wait until fraud happens. Stay one step ahead.
            </h3>
            <p className="text-xs sm:text-sm text-slate-300 leading-relaxed">
              Check whether your email, mobile number, or identity documents have appeared in recent Australian data breaches. Get 24/7 dark web surveillance and real-time scam alerts.
            </p>
          </div>

          <div className="flex flex-col gap-3 w-full sm:w-auto flex-shrink-0">
            <Link
              to="/check-identity"
              className="px-6 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs sm:text-sm shadow-lg shadow-emerald-950/40 transition-colors text-center flex items-center justify-center gap-2"
            >
              <span>Check My Identity Free</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/pricing"
              className="px-6 py-2.5 rounded-xl bg-slate-900 hover:bg-slate-800 border border-slate-700 text-slate-200 font-semibold text-xs transition-colors text-center"
            >
              Start Personal Plan ($9.95/mo)
            </Link>
          </div>
        </div>

        <div className="pt-4 border-t border-slate-800 flex flex-wrap items-center justify-between gap-3 text-xs text-slate-400">
          <span className="flex items-center gap-1.5 text-slate-300">
            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
            <span>Zero Password Collection Policy</span>
          </span>
          <span className="flex items-center gap-1.5 text-slate-300">
            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
            <span>Australian Privacy Principles (APP) Compliant</span>
          </span>
          <span className="flex items-center gap-1.5 text-slate-300">
            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
            <span>Cancel Anytime in 1-Click</span>
          </span>
        </div>
      </div>

      {/* Australian Emergency Helplines Strip */}
      <div className="p-6 rounded-2xl bg-slate-900 border border-slate-800 space-y-3">
        <h4 className="text-xs font-bold text-slate-300 uppercase tracking-wider flex items-center gap-2">
          <PhoneCall className="w-4 h-4 text-emerald-400" />
          <span>Official Australian Emergency Hotlines</span>
        </h4>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
          <a
            href="tel:1800595160"
            className="p-3 rounded-xl bg-slate-950 border border-slate-800 hover:border-emerald-700 transition-colors block"
          >
            <div className="font-bold text-white flex justify-between">
              <span>IDCARE Support</span>
              <span className="text-emerald-400 text-[10px]">FREE</span>
            </div>
            <div className="text-emerald-400 font-mono font-bold">1800 595 160</div>
            <div className="text-[10px] text-slate-400">National Identity Charity</div>
          </a>

          <a
            href="https://www.cyber.gov.au"
            target="_blank"
            rel="noreferrer"
            className="p-3 rounded-xl bg-slate-950 border border-slate-800 hover:border-blue-700 transition-colors block"
          >
            <div className="font-bold text-white flex justify-between">
              <span>ReportCyber</span>
              <ExternalLink className="w-3 h-3 text-slate-400" />
            </div>
            <div className="text-blue-400 font-mono font-bold">cyber.gov.au</div>
            <div className="text-[10px] text-slate-400">Police Cyber Incident Reports</div>
          </a>

          <a
            href="tel:138332"
            className="p-3 rounded-xl bg-slate-950 border border-slate-800 hover:border-purple-700 transition-colors block"
          >
            <div className="font-bold text-white flex justify-between">
              <span>Equifax Credit Ban</span>
              <span className="text-purple-400 text-[10px]">FREE</span>
            </div>
            <div className="text-purple-400 font-mono font-bold">138 332</div>
            <div className="text-[10px] text-slate-400">Credit Bureau Lock</div>
          </a>
        </div>
      </div>

      {/* Related Articles */}
      {relatedArticles.length > 0 && (
        <div className="space-y-4 pt-4 border-t border-slate-800">
          <h3 className="text-base font-bold text-white">Related Security Playbooks</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {relatedArticles.map((rel) => (
              <Link
                key={rel.id}
                to={`/resources/${rel.slug}`}
                className="p-5 rounded-2xl bg-slate-900 border border-slate-800 hover:border-slate-700 transition-colors space-y-2 block text-xs group shadow-lg"
              >
                <div className="flex items-center justify-between text-[10px]">
                  <span className="text-emerald-400 font-bold uppercase">{rel.category}</span>
                  <span className="text-slate-400 font-mono">{rel.readingTimeMinutes} min read</span>
                </div>
                <h4 className="font-bold text-white group-hover:text-emerald-400 transition-colors leading-snug text-sm">
                  {rel.title}
                </h4>
                <p className="text-slate-400 text-[11px] line-clamp-2 leading-relaxed">
                  {rel.summary}
                </p>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
